import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  Grid,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Autocomplete,
} from "@mui/material";
import { useFormik } from "formik";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { useCreateEmailMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import {
  IEmail,
  CustomerOptionString,
} from "../../../Api/Interface/api.interface";
import { useStyles } from "../../../theme/appStyles";
import { emailNotification } from "../../../Core/Enum/enum";
import { validateEmail } from "../../../Components/Validation/validationUtils";
import useIsMobile from "../../../Core/hook/useIsMobile";

interface IDepartmentPopUp {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data?: IEmail;
}

interface FormValues {
  multipleType: string[];
  email: string;
  id?: number;
}

const Department = ({
  openModel = false,
  handleCloseDialog,
  data,
}: IDepartmentPopUp) => {
  const classes = useStyles();
  const [open] = React.useState(openModel);
  const theme = useTheme();
  const [newDepartment, { isLoading }] = useCreateEmailMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const isMobileRes = useIsMobile();

  const handleClose = () => {
    handleCloseDialog(false);
  };

  const emailTypeOptions: CustomerOptionString[] = Object.values(
    emailNotification
  ).map((day) => ({
    label: day,
    value: day,
  }));

  const formik = useFormik<FormValues>({
    initialValues: {
      id: data?.id,
      email: data?.email || "",
      multipleType: data?.multipleType || [],
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: { [key: string]: string } = {};

      const emailError = validateEmail(values.email);
      if (emailError) {
        errors.email = emailError;
      }

      return errors;
    },
    onSubmit: async (values) => {
      try {
        const temData = {
          id: values?.id,
          email: values.email?.trim().toLowerCase(),
          multipleType: values.multipleType,
        };
        if (!data) {
          delete values.id;
        }

        const addCompanyResponse = await newDepartment(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          handleClose();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    return (
      formik.values.multipleType.length > 0 &&
      formik.values.email !== "" &&
      formik.values.email !== undefined
    );
  }, [formik]);

  const gridHeight = isMobileRes ? "30px" : "400px";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby="alert-dialog-description"
      classes={{ paper: "inherit" }}
      fullScreen={isMobileRes}
      fullWidth={isMobileRes}
      maxWidth="lg"
    >
      <Grid className={isMobileRes ? classes.DialogMobile : classes.Dialog}>
        <Grid container spacing={3}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Card className={classes.stepperCard}>
              <Grid
                container
                justifyContent={"space-between"}
                spacing={5}
                sx={{ borderBottom: 2, padding: 2 }}
                className={classes.titleGrid}
              >
                <Grid item pt={2} alignContent={"center"}>
                  <Typography className={classes.cardTitle}>
                    {data ? "Edit" : "Add"}
                  </Typography>
                </Grid>
                <Grid
                  item
                  pt={2}
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                  
                  <Button
                    variant="contained"
                    className={classes.editButton}
                    onClick={handleClose}
                  >
                    <CloseIcon />
                  </Button>
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <form onSubmit={formik.handleSubmit}>
              <Grid container>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Card className={classes.card}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                          Email Setting
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3} p={"24px"}>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Email{" "}
                              <span className={classes.requiedfield}>*</span>
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="E-mail"
                              size="small"
                              type="email"
                              {...formik.getFieldProps("email")}
                              className={classes.textField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              error={
                                formik.touched.email &&
                                Boolean(formik.errors.email)
                              }
                              helperText={
                                formik.touched.email && formik.errors.email
                              }
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Type{" "}
                              <span className={classes.requiedfield}>*</span>
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Autocomplete
                              multiple
                              options={emailTypeOptions}
                              getOptionLabel={(option: CustomerOptionString) =>
                                option.label
                              }
                              value={emailTypeOptions.filter((opt) =>
                                formik.values.multipleType?.includes(opt.value)
                              )}
                              onChange={(
                                _,
                                newValue: CustomerOptionString[]
                              ) => {
                                const selectedValues = newValue.map(
                                  (v) => v.value
                                );
                                formik.setFieldValue(
                                  "multipleType",
                                  selectedValues
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Select types..."
                                  size="small"
                                  InputLabelProps={{ shrink: true }}
                                  className="textField"
                                />
                              )}
                              sx={{ width: "100%" }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                  <Card className={classes.card}>
                    <Grid container spacing={3} p={"24px"}>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<CloseIcon />}
                          className={classes.addButton}
                          onClick={handleClose}
                        >
                          Cancel
                        </Button>
                        <Box m={0.5}></Box>

                        <Button
                          variant="contained"
                          startIcon={
                            isLoading ? (
                              <CircularProgress
                                size={18}
                                className={classes.isLoading}
                              />
                            ) : (
                              <CheckCircleOutlineIcon/>
                            )
                          }
                          className={classes.addButton}
                          onClick={() => formik.handleSubmit()}
                          disabled={!formValid || isLoading}
                        >
                           {data ? "Update" : "Create"}
                        </Button>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default Department;
